<template>
  <div
    class="checkout__canvas-3d"
    id="canvas__container"
    style="height: 400px"
    ref="canvas"
  >
    <div class="renderer" id="3mf-preview" ref="renderer" />
    <b-loading v-model="isLoading" :is-full-page="false"></b-loading>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { eventBus } from "../../events/event-bus";

import * as THREE from "three";
import { ThreeMFLoader } from "three/examples/jsm/loaders/3MFLoader";
import { TrackballControls } from "three/examples/jsm/controls/TrackballControls";

export default {
  name: "Canvas3D",
  data() {
    return {
      isLoading: true,
      loader: null,
      renderer: null,
      scene: null,
      camera: null,
      controls: null,
      meshObject: null,
      edges: null,
      domElement: null,
      frontImageData: null,
    };
  },
  computed: {
    ...mapGetters([
      "current3DFile",
      "currentCartItem",
      "currentConfigure3DStep",
    ]),
    fileUrl() {
      if (this.current3DFile.files)
        return `${process.env.VUE_APP_FULFILLMENT_API_BASE_URL}/analytx${this.current3DFile.files["3mf"]}`;
    },
    cartItemColor() {
      if (this.currentCartItem.color) return this.currentCartItem.color.hex;
      else {
        return "#eeeeee";
      }
    },
    edgeColor() {
      let hex = this.cartItemColor;
      const r = parseInt(hex[1] + hex[2], 16);
      const g = parseInt(hex[3] + hex[4], 16);
      const b = parseInt(hex[5] + hex[6], 16);
      const brightness = Math.round((r * 299 + g * 587 + b * 114) / 1000);
      return brightness > 125 ? "0x000000" : "0xffffff";
    },
  },
  watch: {
    cartItemColor(value) {
      if (typeof value === "string" && this.meshObject) {
        if (this.meshObject) {
          this.meshObject.traverse((child) => {
            if (child.isMesh) {
              let colors = this.parseHexToRGB(value);

              child.material.color.r = colors.r;
              child.material.color.g = colors.g;
              child.material.color.b = colors.b;
              child.material.needsUpdate = true;
            }
          });
        }
      }
    },
    fileUrl(value) {
      if (value && typeof value === "string") {
        this.loadObject();
      }
    },
    currentConfigure3DStep() {
      // width of canvas container might change during checkout-step, the next line takes care of that
      this.onWindowResize();
    },
  },
  methods: {
    ...mapActions(["updateCurrentCartItem"]),
    parseHexToRGB(hexColor) {
      let colorObject = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(
        hexColor
      );
      return colorObject
        ? {
            r: parseInt(colorObject[1], 16) / 255,
            g: parseInt(colorObject[2], 16) / 255,
            b: parseInt(colorObject[3], 16) / 255,
          }
        : null;
    },
    setupRenderer(width = 400, height = 400) {
      this.renderer = new THREE.WebGLRenderer({
        antialias: true,
        alpha: true,
        preserveDrawingBuffer: true,
      });
      this.renderer.setSize(width, height);
      this.domElement = this.renderer.domElement;
      this.$refs.renderer.appendChild(this.renderer.domElement);
      this.storeFrontImageData();
    },
    setupScene() {
      this.scene = new THREE.Scene();
      this.scene.add(new THREE.HemisphereLight(0x333333, 0x333333));
    },
    setupCamera(width = 400, height = 400) {
      this.camera = new THREE.PerspectiveCamera(40, 1, 0.1, 100000);
      this.camera.aspect = width / height;
      this.camera.updateProjectionMatrix();
      this.scene.add(this.camera);

      let alight = new THREE.AmbientLight(0xffffff, 0.2);
      this.scene.add(alight);

      let pointLight = new THREE.PointLight(0xffffff, 0.8);
      pointLight.position.set(0, 0, 0);

      this.camera.add(pointLight);
    },
    setupControls() {
      this.controls = new TrackballControls(this.camera, this.domElement);
      this.controls.rotateSpeed = 2.2;
      this.controls.zoomSpeed = 1.2;
      this.controls.panSpeed = 0.8;
    },
    animate() {
      let vm = this;
      requestAnimationFrame(vm.animate);
      vm.renderer.render(vm.scene, vm.camera);
      vm.frontImageData = vm.renderer.domElement
        .toDataURL("image/png")
        .replace(/^data:image\/(png|jpg|jpeg);base64,/, "");
      if (vm.controls) {
        vm.controls.update();
      }
    },
    onWindowResize() {
      const { clientWidth, clientHeight } = document.getElementById(
        "canvas__container"
      );
      this.renderer.setSize(clientWidth || 400, clientHeight || 400);
      this.camera.aspect = (clientWidth || 400) / (clientHeight || 400);
      this.camera.updateProjectionMatrix();
    },
    async loadObject() {
      let vm = this;
      vm.loader = new ThreeMFLoader();
      this.setupControls();

      vm.loader.load(vm.fileUrl, (object) => {
        this.isLoading = false;
        let box = new THREE.Box3().setFromObject(object);

        object.position.x = (box.max.x + box.min.x) / -2;
        object.position.y = (box.max.y + box.min.y) / -2;
        object.position.z = (box.max.z + box.min.z) / -2;

        if (vm.meshObject) {
          vm.scene.remove(vm.meshObject);
        }

        if (vm.edges) {
          vm.scene.remove(vm.edges);
        }
        vm.meshObject = object;

        if (vm.meshObject) {
          vm.meshObject.traverse((child) => {
            if (child.isMesh) {
              let color = this.cartItemColor;
              if (!color) color = "#ffffff";
              child.material.color.setHex(color.replace("#", "0x"));
              child.material.vertexColors = false;
              child.material.needsUpdate = true;

              const edges = new THREE.EdgesGeometry(child.geometry);
              const line = new THREE.LineSegments(
                edges,
                new THREE.LineBasicMaterial({
                  color: this.edgeColors,
                  linewidth: 1,
                  opacity: 0.1,
                  transparent: true,
                })
              );
              this.edges = line;
              //vm.scene.add(line);
            }
          });
        }

        vm.scene.add(vm.meshObject);

        // calculate a nice position for the camera
        let r =
          Math.sqrt(
            Math.pow(box.max.x - box.min.x, 2) +
              Math.pow(box.max.y - box.min.y, 2) +
              Math.pow(box.max.z - box.min.z, 2)
          ) / 2.0;
        let d = r / Math.sin((20 * Math.PI) / 180);
        let x = Math.sin((20 * Math.PI) / 180) * d;
        let y = Math.cos((20 * Math.PI) / 180) * d;

        vm.camera.up.set(0, 0, 1);
        vm.camera.position.set(x, -y, (box.max.z - box.min.z) / 2);

        vm.controls.handleResize();
      });
    },
    async storeFrontImageData() {
      let renderer = this.renderer;
      if (renderer) {
        await this.updateCurrentCartItem({
          frontImageData: renderer.domElement.toDataURL("image/png"),
        });
      }
    },
  },
  mounted() {
    const { clientWidth, clientHeight } = document.getElementById(
      "canvas__container"
    );

    this.setupScene();
    this.setupRenderer(clientWidth, clientHeight);
    this.setupCamera(clientWidth, clientHeight);
    this.setupControls();
    this.loadObject();
    this.animate();

    window.addEventListener("resize", this.onWindowResize, false);

    let vm = this;
    eventBus.$on("triggeredAddItemToCart", this.storeFrontImageData);
  },
  async beforeDestroy() {
    await this.storeFrontImageData();
  },
};
</script>
<style lang="scss">
.checkout__canvas-3d {
  cursor: grab;
  display: flex;
  justify-content: center;
  position: relative;
}
</style>
